import React, { memo, useState, useEffect } from 'react';
import { View, Platform, Linking } from 'react-native';
import { QText, Button } from './index';
import { useTheme } from 'react-native-paper';
import { useGetAttachmentByIdQuery } from '../store/api/QueueApi';

/**
 * PDFViewer component that opens PDF files in browser
 * Simple and reliable approach for all platforms
 */
const PDFViewer = ({ uri, attachmentId, style, onError }) => {
  const theme = useTheme();
  const [pdfUri, setPdfUri] = useState(uri);
  const [isLoading, setIsLoading] = useState(!uri);

  // Use RTK Query to fetch the attachment by ID if no URI provided
  const {
    data: fileData,
    error,
    isLoading: isQueryLoading,
  } = useGetAttachmentByIdQuery(attachmentId, {
    skip: !!uri || !attachmentId,
  });

  useEffect(() => {
    if (fileData && !uri) {
      try {
        // Convert the file data to a blob URL for display
        const binaryData = fileData.data || fileData;

        if (Array.isArray(binaryData)) {
          // Convert array of numbers to Uint8Array
          const uint8Array = new Uint8Array(binaryData);
          const blob = new Blob([uint8Array], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          setPdfUri(blobUrl);
        } else if (binaryData instanceof Blob) {
          const blobUrl = URL.createObjectURL(binaryData);
          setPdfUri(blobUrl);
        } else {
          console.error('Unexpected PDF data format:', typeof binaryData);
          if (onError) onError(new Error('Invalid PDF data format'));
        }
      } catch (err) {
        console.error('Error processing PDF data:', err);
        if (onError) onError(err);
      }
      setIsLoading(false);
    }
  }, [fileData, uri, onError]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUri && pdfUri.startsWith('blob:') && pdfUri !== uri) {
        URL.revokeObjectURL(pdfUri);
      }
    };
  }, [pdfUri, uri]);

  const handleOpenInBrowser = () => {
    if (pdfUri) {
      Linking.openURL(pdfUri).catch((err) => {
        console.error('Error opening PDF in browser:', err);
        if (onError) onError(err);
      });
    }
  };

  const handleDownload = () => {
    if (pdfUri) {
      try {
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = pdfUri;
        link.download = 'document.pdf'; // Default filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Error downloading PDF:', err);
        if (onError) onError(err);
      }
    }
  };

  if (isLoading || isQueryLoading) {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
          },
        ]}
      >
        <QText style={{ color: theme.colors.onSurface }}>Loading PDF...</QText>
      </View>
    );
  }

  if (error || !pdfUri) {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
          },
        ]}
      >
        <QText style={{ color: theme.colors.error, textAlign: 'center' }}>
          Failed to load PDF: {error?.message || 'Unknown error'}
        </QText>
      </View>
    );
  }

  // Simple approach: show a button to open PDF in browser
  return (
    <View
      style={[
        style,
        {
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.surface,
          maxWidth: 600,
          alignSelf: 'center',
          width: '100%',
          padding: 20,
        },
      ]}
    >
      <QText
        style={{
          color: theme.colors.onSurface,
          textAlign: 'center',
          marginBottom: 16,
        }}
      >
        PDF Document Ready
      </QText>
      <QText
        style={{
          color: theme.colors.onSurface,
          textAlign: 'center',
          marginBottom: 24,
        }}
      >
        Click the button below to open this PDF in your browser or default PDF
        viewer.
      </QText>
      <Button
        mode="contained"
        onPress={handleOpenInBrowser}
        style={{ marginBottom: 16 }}
      >
        Open PDF in Browser
      </Button>
      {Platform.OS === 'web' && (
        <QText
          style={{
            color: theme.colors.primary,
            textAlign: 'center',
            fontSize: 12,
            marginBottom: 16,
          }}
        >
          The PDF will open in a new tab
        </QText>
      )}
      <Button
        mode="outlined"
        onPress={handleDownload}
        style={{ marginBottom: 16 }}
      >
        Download
      </Button>
    </View>
  );
};

export default memo(PDFViewer);
